'use client';

import React from 'react';
import Link from 'next/link';
import Header from '@/components/common/Header';
import Footer from '@/app/homepage/components/SiteFooter';

export default function AboutPage() {
  const stats = [
    { value: '500+', label: 'Real Estate Clients' },
    { value: '50K+', label: 'Images Edited Monthly' },
    { value: '24–48h', label: 'Average Turnaround' },
    { value: '4.9★', label: 'Average Client Rating' },
  ];

  const services = [
    {
      title: 'HDR Blending',
      desc: 'Balanced exposures with crisp windows, natural light, and true-to-life colour.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      title: 'Day to Dusk',
      desc: 'Transform flat daytime shots into warm, golden-hour twilight scenes that sell.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12h1m8-9v1m8 8h1M5.6 5.6l.7.7m12.1-.7l-.7.7M8 15a4 4 0 118 0H8zM3 19h18" />
        </svg>
      ),
    },
    {
      title: 'Virtual Staging',
      desc: 'Furnish empty rooms with photorealistic, style-matched decor buyers love.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
    },
    {
      title: 'Object & Clutter Removal',
      desc: 'Remove cars, wires, bins, and clutter for clean, distraction-free listings.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      ),
    },
    {
      title: 'Sky Replacement',
      desc: 'Swap dull, grey skies for vibrant blues or dramatic sunsets in seconds.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
        </svg>
      ),
    },
    {
      title: 'Virtual Renovation',
      desc: 'Preview remodels — new floors, paint, and finishes — before a single nail.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      ),
    },
    {
      title: 'Floor Plan Redraw',
      desc: '2D and 3D floor plans redrawn clean, branded, and buyer-ready.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm6 0v6m0 0H4m6 0h10M10 11v9" />
        </svg>
      ),
    },
    {
      title: 'Photo Enhancement',
      desc: 'Colour correction, lens fixes, sharpening, and perspective straightening.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      ),
    },
  ];

  const steps = [
    { num: '01', title: 'Upload', desc: 'Drag and drop your RAW or JPEG photos into your dashboard — any volume, any format.' },
    { num: '02', title: 'We Edit', desc: 'Expert editors hand-process each image to your style guide and quality standards.' },
    { num: '03', title: 'Review', desc: 'Preview edits, request unlimited revisions, and approve with a single click.' },
    { num: '04', title: 'Download', desc: 'Grab full-resolution, listing-ready images delivered in 24–48 hours.' },
  ];

  const differentiators = [
    {
      title: 'Real Human Editors',
      desc: 'No one-click filters. Skilled retouchers who understand real estate composition and light.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
    },
    {
      title: 'Consistent Turnaround',
      desc: 'Reliable 24–48 hour delivery, even at scale, so your listings never wait.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      title: 'Unlimited Revisions',
      desc: "We refine until it's right — revisions are always included, never extra.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      ),
    },
    {
      title: 'Pay Per Image',
      desc: 'No subscriptions, no minimums. Transparent pricing from just $0.40 per photo.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      title: 'Enterprise Security',
      desc: 'Your files are encrypted, access-controlled, and never shared or resold.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
    },
    {
      title: 'One Simple Dashboard',
      desc: 'Track every order, team member, and download from a single, tidy workspace.',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      ),
    },
  ];

  const values = [
    { title: 'Quality First', desc: 'Every image gets meticulous, hand-crafted attention. We never compromise on the final result.' },
    { title: 'Client Success', desc: 'Your listings selling faster is our real metric. We win when you win.' },
    { title: 'Innovation', desc: 'We invest in the latest AI-assisted and manual techniques to stay ahead of the curve.' },
    { title: 'Transparency', desc: 'Honest, upfront pricing and clear communication. No hidden fees, no surprises, ever.' },
  ];

  const testimonials = [
    {
      quote: 'Shashio turned around 40 twilight edits overnight before my open house. The listing sold in four days.',
      name: 'Marcus R.',
      role: 'Listing Agent, Austin',
    },
    {
      quote: 'The virtual staging is genuinely photorealistic. Buyers cannot tell it is not real furniture, and that sells homes.',
      name: 'Priya S.',
      role: 'Broker, Toronto',
    },
    {
      quote: 'We scaled from 5 to 30 shoots a week and Shashio kept every single delivery on time. Total game-changer.',
      name: 'Daniel K.',
      role: 'Real Estate Photographer, London',
    },
  ];

  return (
    <div className="bg-[#0B0D17] text-white">
      <Header />

      {/* ===================== HERO ===================== */}
      <section className="relative overflow-hidden bg-[#0B0D17]">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/3 w-[1000px] h-[1000px] bg-purple-600/10 rounded-full blur-[160px] animate-glow-breathe" />
          <div className="absolute bottom-0 right-1/3 w-[800px] h-[800px] bg-blue-600/5 rounded-full blur-[140px] animate-glow-breathe-delayed" />
          <div
            className="absolute inset-0 opacity-[0.025]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-28 lg:py-36 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.12] backdrop-blur-sm text-purple-200 text-xs font-medium mb-8">
            <span className="text-sm">🏡</span>
            <span>About Shashio · Real Estate Photo Editing</span>
          </div>

          <h1 className="text-5xl lg:text-7xl font-bold leading-[1.05] tracking-tight mb-6">
            We make every property
            <br />
            <span className="bg-gradient-to-r from-purple-400 via-purple-300 to-blue-400 bg-clip-text text-transparent">
              look its absolute best.
            </span>
          </h1>
          <p className="max-w-3xl mx-auto text-xl text-gray-400 leading-relaxed">
            Shashio is a photo-editing partner built exclusively for real estate. From HDR blending to virtual
            staging, we help agents, brokers, and photographers turn raw shots into scroll-stopping, sale-ready
            imagery — fast, affordable, and consistently on brand.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
            <Link href="/auth/signup">
              <button className="group px-8 py-4 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-bold rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-xl shadow-purple-500/30 hover:shadow-purple-500/50 hover:-translate-y-0.5">
                Start Free Trial
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </Link>
            <Link href="/services">
              <button className="px-8 py-4 bg-white/[0.03] hover:bg-white/[0.08] backdrop-blur-sm text-white font-semibold rounded-xl transition-all duration-300 border border-white/[0.15] hover:border-white/30">
                Explore Services
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* ===================== STATS ===================== */}
      <section className="relative py-16 px-6 lg:px-12 border-y border-white/[0.06]">
        <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((s, i) => (
            <div
              key={i}
              className="text-center bg-white/[0.03] border border-white/[0.08] rounded-2xl p-8 hover:border-purple-500/30 transition-all duration-300"
            >
              <div className="text-4xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent mb-2">
                {s.value}
              </div>
              <div className="text-sm text-gray-400 font-medium">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ===================== OUR STORY ===================== */}
      <section className="relative py-24 lg:py-32 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-bold">
              OUR STORY
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight">
              Professional editing,{' '}
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                without the friction.
              </span>
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              Shashio started with a frustration we heard again and again: editing was either too expensive, too
              slow, or too inconsistent to rely on. Agents were losing listings while photos sat in a queue.
            </p>
            <p className="text-lg text-gray-400 leading-relaxed">
              So we built a better model — a dedicated team of real estate retouchers, a dead-simple dashboard,
              transparent per-image pricing, and turnaround measured in hours, not days.
            </p>
            <p className="text-lg text-gray-400 leading-relaxed">
              Today we edit tens of thousands of images every month for professionals across the globe, helping
              them present properties beautifully and close deals faster.
            </p>
            <div className="pt-2">
              <Link href="/auth/signup">
                <button className="px-8 py-3.5 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-bold rounded-xl transition-all duration-300 shadow-lg shadow-purple-500/30 hover:-translate-y-0.5">
                  Join Our Community
                </button>
              </Link>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 bg-purple-600/15 rounded-3xl blur-[80px] -z-10" />
            <div className="grid gap-5">
              {[
                { big: '50% OFF', small: 'New lower pricing across all services' },
                { big: '$0.40', small: 'Starting price per edited image' },
                { big: '24–48h', small: 'Standard delivery, every order' },
              ].map((c, i) => (
                <div
                  key={i}
                  className="bg-[#13151F]/95 backdrop-blur-2xl rounded-2xl border-2 border-white/[0.12] p-8 flex items-center justify-between hover:border-purple-500/30 transition-all duration-300"
                >
                  <div className="text-4xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                    {c.big}
                  </div>
                  <div className="text-gray-400 text-right max-w-[55%]">{c.small}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===================== WHAT WE DO ===================== */}
      <section className="relative py-24 lg:py-32 px-6 lg:px-12 bg-white/[0.015] border-y border-white/[0.06]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-bold mb-6">
              WHAT WE DO
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">
              Every edit a listing needs
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              One partner for the full spectrum of real estate image work — shot on any camera, delivered listing-ready.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {services.map((s, i) => (
              <div
                key={i}
                className="group bg-[#13151F]/80 backdrop-blur-sm rounded-2xl border border-white/[0.08] p-6 hover:border-purple-500/30 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/20 to-blue-500/10 border border-purple-500/20 flex items-center justify-center text-purple-300 mb-4 group-hover:scale-110 transition-transform">
                  {s.icon}
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{s.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== HOW IT WORKS ===================== */}
      <section className="relative py-24 lg:py-32 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-bold mb-6">
              HOW IT WORKS
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">
              Three steps. Zero friction.
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              From upload to download, we keep it effortless so you can focus on selling.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <div
                key={i}
                className="relative bg-white/[0.03] border border-white/[0.08] rounded-2xl p-8 hover:border-purple-500/30 transition-all duration-300"
              >
                <div className="text-5xl font-bold bg-gradient-to-r from-purple-500/40 to-blue-500/30 bg-clip-text text-transparent mb-4">
                  {step.num}
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{step.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== WHY SHASHIO ===================== */}
      <section className="relative py-24 lg:py-32 px-6 lg:px-12 bg-white/[0.015] border-y border-white/[0.06]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-bold mb-6">
              WHY SHASHIO
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">
              Built for how you actually work
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              The details that make Shashio the reliable editing partner professionals stick with.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {differentiators.map((d, i) => (
              <div
                key={i}
                className="group flex items-start gap-4 bg-[#13151F]/80 backdrop-blur-sm rounded-2xl border border-white/[0.08] p-6 hover:border-purple-500/30 transition-all duration-300"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/20 to-blue-500/10 border border-purple-500/20 flex items-center justify-center text-purple-300 group-hover:scale-110 transition-transform">
                  {d.icon}
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg mb-1.5">{d.title}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">{d.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== VALUES ===================== */}
      <section className="relative py-24 lg:py-32 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-bold mb-6">
              OUR VALUES
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">
              What drives us
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              The principles behind every image we touch and every client we serve.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <div
                key={i}
                className="relative bg-white/[0.03] border border-white/[0.08] rounded-2xl p-8 hover:border-purple-500/30 transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white font-bold mb-5 shadow-lg shadow-purple-500/30">
                  {i + 1}
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{v.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== TESTIMONIALS ===================== */}
      <section className="relative py-24 lg:py-32 px-6 lg:px-12 bg-white/[0.015] border-y border-white/[0.06]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-bold mb-6">
              LOVED BY PROS
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight">
              Trusted by real estate professionals
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className="bg-[#13151F]/80 backdrop-blur-sm rounded-2xl border border-white/[0.08] p-8 hover:border-purple-500/30 transition-all duration-300 flex flex-col"
              >
                <div className="flex items-center gap-0.5 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className="w-4 h-4 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-300 leading-relaxed flex-1">&ldquo;{t.quote}&rdquo;</p>
                <div className="flex items-center gap-3 mt-6 pt-6 border-t border-white/[0.08]">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-blue-400 flex items-center justify-center text-white text-sm font-semibold">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <div className="text-white text-sm font-semibold">{t.name}</div>
                    <div className="text-xs text-gray-500">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== CTA ===================== */}
      <section className="relative py-24 lg:py-32 px-6 lg:px-12 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] bg-purple-600/10 rounded-full blur-[160px] animate-glow-breathe" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="relative bg-[#13151F]/95 backdrop-blur-2xl rounded-3xl border-2 border-white/[0.12] shadow-[0_30px_80px_rgba(0,0,0,0.6),0_10px_30px_rgba(124,58,237,0.15)] p-12 lg:p-16">
            <div className="absolute -inset-4 bg-purple-600/15 rounded-3xl blur-[80px] -z-10" />
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-5">
              Ready to make your listings{' '}
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                shine?
              </span>
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed mb-10 max-w-2xl mx-auto">
              Join hundreds of real estate professionals who trust Shashio. Create your free account and see the
              difference expert editing makes on your very next shoot.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/auth/signup">
                <button className="group px-10 py-4 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-bold text-lg rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-xl shadow-purple-500/30 hover:shadow-purple-500/50 hover:-translate-y-0.5">
                  Create Free Account
                  <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </button>
              </Link>
              <Link href="/contact">
                <button className="px-10 py-4 bg-white/[0.03] hover:bg-white/[0.08] backdrop-blur-sm text-white font-bold text-lg rounded-xl transition-all duration-300 border border-white/[0.15] hover:border-white/30">
                  Contact Us
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Animations */}
      <style jsx global>{`
        @keyframes glow-breathe {
          0%, 100% { opacity: 0.1; transform: scale(1); }
          50% { opacity: 0.15; transform: scale(1.05); }
        }
        @keyframes glow-breathe-delayed {
          0%, 100% { opacity: 0.05; transform: scale(1); }
          50% { opacity: 0.08; transform: scale(1.03); }
        }
        .animate-glow-breathe { animation: glow-breathe 8s ease-in-out infinite; }
        .animate-glow-breathe-delayed { animation: glow-breathe-delayed 10s ease-in-out infinite; animation-delay: 2s; }
      `}</style>
    </div>
  );
}
